import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { z } from "zod";
import type { ScopeState } from "../../data-center/components/scope-selector";
import type { ExpedienteConditionData } from "../components/condition";
import type { LogicalOperator, Variable as IfVariable } from "../../if/types";
import type {
  ActionItemType,
  ValueColumn,
  ValueInputType,
  RegistryInputMode,
  LinkActionType,
  LinkUserEntry,
  LinkExpedientsActionType,
  LinkExpedientEntry,
} from "../types";
import type { EntityRoleType } from "@/schemas/digital-files/entity";
import type { Variable, NodeVariable } from "../../types";
import type { ApiNode } from "@/workflow_api/types/workflow";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { useUpdateNode } from "@/workflow_api/services/nodes.api";
import { useGetNode } from "@/workflow_api/services/nodes.api";
import { actionList, type ActionItem } from "../components/action-selector";
import {
  getDeserializedExpedienteColumns,
  getExpedienteDataFromNode,
  getDeserializedLinkUsers,
  getDeserializedRegistryName,
  getDeserializedLinkExpedients,
} from "./deserialization";
import { getSerializedNode } from "./serialization";
import { useGetVariables } from "@/workflow_api/hooks/variables/useGetVariables";
import { isValidNumericRange } from "@/utils/format";
import {
  useGetAllRegistriesForWorkflow,
  type ExpedienteDropdownItem,
  type RegistryDropdownItem,
} from "@/queries/digital-files/registries.api";
import type { RegistryType } from "@/schemas/digital-files/registry";
import {
  useGetEntities,
  useGetEntity,
} from "@/queries/digital-files/entities.api";
import type { FieldType, EntityType } from "@/schemas/digital-files";

// Helper function to get placeholder text based on column type
const getPlaceholderForType = (type: ValueInputType): string => {
  switch (type) {
    case "string":
      return "Escribe un texto";
    case "number":
      return "Escribe un número";
    case "date":
      return "Selecciona una fecha";
    case "boolean":
      return "Elige verdadero o falso";
    case "variable":
      return "Elige una variable";
    case "regex":
      return "Escribe una expresión regular";
    case "positive-integer":
      return "Escribe un número entero positivo";
    default:
      return "Ingresa un valor";
  }
};

// Helper function to map entity field_type to ValueInputType
const mapEntityFieldTypeToValueInputType = (
  fieldType: string,
): ValueInputType => {
  switch (fieldType.toLowerCase()) {
    case "text":
    case "textarea":
    case "email":
    case "phone":
    case "select":
    case "multiselect":
    case "radio":
    case "identification":
    case "url":
      return "string";
    case "number":
    case "currency":
      return "number";
    case "date":
    case "datetime":
    case "time":
    case "date_range":
    case "datetime_range":
      return "date";
    case "checkbox":
      return "boolean";
    default:
      return "string";
  }
};

// Helper function to convert ValueInputType to VariableType for Variable definitions
const mapValueInputTypeToVariableType = (
  valueInputType: ValueInputType,
):
  | "string"
  | "number"
  | "date"
  | "boolean"
  | "array"
  | "file"
  | "select"
  | "float" => {
  switch (valueInputType) {
    case "string":
    case "regex":
      return "string";
    case "number":
    case "positive-integer":
      return "number";
    case "date":
      return "date";
    case "boolean":
      return "boolean";
    case "variable":
      return "string"; // Variables default to string
    default:
      return "string";
  }
};

const initScopeState: ScopeState = {
  comparativeValue: "",
  operationType: "input",
  scope: {
    id: "created_at",
    name: "Fecha de creación",
    type: "dateScope",
    data_type: "date",
  },
  conditionScope: { name: "ANY", label: "Cualquiera" },
  initialRange: { value: "", type: "input" },
  endRange: { value: "", type: "input" },
  dateRangeError: "",
};

// Helper function to get action from node inputs
const getExpedienteNodeAction = (
  node: ApiNode,
  actions: ActionItem[],
): ActionItem | undefined => {
  const actionInput = node.inputs?.find((input) => input.name === "operation");
  if (actionInput) {
    const action = actions.find(
      (a) =>
        a.id?.toLowerCase() === actionInput.value?.toString().toLowerCase(),
    );
    if (action) {
      return action;
    }
  }
};

// Zod: schema & derived validation state for variable name
const variableNameSchema = z
  .string()
  .trim()
  .min(1, "El nombre de la variable es requerido")
  .max(255, "Máximo 255 caracteres")
  .regex(
    /^[A-Za-z][A-Za-z0-9_-]*$/,
    "El nombre de la variable debe iniciar con una letra y solo puede contener letras, números y guiones.",
  );

const ExpedienteNodeContext = createContext<{
  scopeState: ScopeState;
  setScopeState: (state: ScopeState) => void;
  isValidScope: boolean;
  multipleSelected: boolean;
  setMultipleSelected: (value: boolean) => void;
  multipleCount: number;
  setMultipleCount: (value: number) => void;
  variableName: string;
  setVariableName: (name: string) => void;
  isVariableNameValid: boolean;
  variableNameError: string | null;
  reset: () => void;
  conditions: ExpedienteConditionData[];
  setConditions: (conditions: ExpedienteConditionData[]) => void;
  logicalOperator: LogicalOperator;
  setLogicalOperator: (operator: LogicalOperator) => void;
  conditionsAreValid: boolean;
  selectedRegistry: RegistryDropdownItem | undefined;
  setSelectedRegistry: (registry: RegistryDropdownItem) => void;
  dropdownItems: ExpedienteDropdownItem[];
  selectedAction: ActionItem | undefined;
  setSelectedAction: (action: ActionItem | undefined) => void;
  columns: ValueColumn[];
  setColumns: (columns: ValueColumn[]) => void;
  columnsAreValid: boolean;
  columnsDef: Variable[];
  setColumnsDef: (columns: Variable[]) => void;
  currentNode: ApiNode | null;
  setCurrentNode: (node: ApiNode | null) => void;
  isReadyToShow: boolean;
  isLoadingTableDetail: boolean;
  saveNode: (title: string) => void;
  nodeName: string | undefined;
  // pagination for registries
  loadMoreRegistries: () => void;
  hasMoreRegistries: boolean;
  isLoadingMoreRegistries: boolean;
  // search for registries
  registrySearchQuery: string;
  setRegistrySearchQuery: (query: string) => void;
  // entity fields for output
  entityFields: FieldType[];
  isLoadingEntityFields: boolean;
  // NEW: Entity selection (step 1)
  entities: EntityType[];
  isLoadingEntities: boolean;
  selectedEntity: EntityType | undefined;
  setSelectedEntity: (entity: EntityType | undefined) => void;
  // NEW: Registry ID input (step 2)
  registryInputMode: RegistryInputMode;
  setRegistryInputMode: (mode: RegistryInputMode) => void;
  registryIdValue: string;
  setRegistryIdValue: (value: string) => void;
  registryVariable: IfVariable | null;
  setRegistryVariable: (variable: IfVariable | null) => void;
  // NEW: Registry Name input
  registryName: string;
  setRegistryName: (name: string) => void;
  registryNameInputType: "inp" | "var";
  setRegistryNameInputType: (type: "inp" | "var") => void;
  registryNameVariable: IfVariable | null;
  setRegistryNameVariable: (variable: IfVariable | null) => void;
  registryNameIsValid: boolean;
  // NEW: Available variables for registry input
  inputVariables: NodeVariable[];
  // All variables (unfiltered) for column inputs
  allInputVariables: NodeVariable[];
  // Link Users state
  linkAction: LinkActionType;
  setLinkAction: (action: LinkActionType) => void;
  linkUsers: LinkUserEntry[];
  setLinkUsers: (users: LinkUserEntry[]) => void;
  entityRoles: EntityRoleType[];
  isLoadingEntityRoles: boolean;
  linkUsersAreValid: boolean;
  // Link Expedients (Registros) state
  linkExpedientsAction: LinkExpedientsActionType;
  setLinkExpedientsAction: (action: LinkExpedientsActionType) => void;
  linkExpedients: LinkExpedientEntry[];
  setLinkExpedients: (expedients: LinkExpedientEntry[]) => void;
  linkExpedientsAreValid: boolean;
  // Link Sub-Action selector (users vs expedients)
  linkSubAction: "link_users" | "link_expedients";
  setLinkSubAction: (subAction: "link_users" | "link_expedients") => void;
}>({
  scopeState: initScopeState,
  setScopeState: () => {},
  isValidScope: true,
  multipleSelected: false,
  setMultipleSelected: () => {},
  multipleCount: 50,
  setMultipleCount: () => {},
  variableName: "",
  setVariableName: () => {},
  isVariableNameValid: true,
  variableNameError: null,
  reset: () => {},
  conditions: [],
  setConditions: () => {},
  logicalOperator: "and",
  setLogicalOperator: () => {},
  conditionsAreValid: true,
  selectedAction: undefined,
  selectedRegistry: undefined,
  setSelectedRegistry: () => {},
  dropdownItems: [],
  setSelectedAction: () => {},
  columns: [],
  setColumns: () => {},
  columnsAreValid: true,
  columnsDef: [],
  setColumnsDef: () => {},
  currentNode: null,
  setCurrentNode: () => {},
  isReadyToShow: false,
  isLoadingTableDetail: false,
  saveNode: () => {},
  nodeName: undefined,
  loadMoreRegistries: () => {},
  hasMoreRegistries: false,
  isLoadingMoreRegistries: false,
  registrySearchQuery: "",
  setRegistrySearchQuery: () => {},
  entityFields: [],
  isLoadingEntityFields: false,
  // NEW: Entity selection defaults
  entities: [],
  isLoadingEntities: false,
  selectedEntity: undefined,
  setSelectedEntity: () => {},
  // NEW: Registry ID input defaults
  registryInputMode: "inp",
  setRegistryInputMode: () => {},
  registryIdValue: "",
  setRegistryIdValue: () => {},
  registryVariable: null,
  setRegistryVariable: () => {},
  // NEW: Registry Name input defaults
  registryName: "",
  setRegistryName: () => {},
  registryNameInputType: "inp",
  setRegistryNameInputType: () => {},
  registryNameVariable: null,
  setRegistryNameVariable: () => {},
  registryNameIsValid: true,
  inputVariables: [],
  allInputVariables: [],
  // Link Users defaults
  linkAction: "link",
  setLinkAction: () => {},
  linkUsers: [],
  setLinkUsers: () => {},
  entityRoles: [],
  isLoadingEntityRoles: false,
  linkUsersAreValid: false,
  // Link Expedients defaults
  linkExpedientsAction: "link",
  setLinkExpedientsAction: () => {},
  linkExpedients: [],
  setLinkExpedients: () => {},
  linkExpedientsAreValid: false,
  // Link Sub-Action defaults
  linkSubAction: "link_users",
  setLinkSubAction: () => {},
});

export const ExpedienteNodeProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { flowId } = useWorkflowStore();
  const { variables: inputVariables, isLoading: loadingVariables } =
    useGetVariables();

  const { selectedNodeId, nodeDetailsOpen } = useUIStore();

  // Exclude the current node's own output variables from available inputs
  const externalInputVariables = useMemo(() => {
    if (!inputVariables) return [];
    return inputVariables.filter(
      (nodeVar) => nodeVar.nodeId !== selectedNodeId,
    );
  }, [inputVariables, selectedNodeId]);

  // Filter variables to only show string and number types for Registry ID
  const filteredInputVariables = useMemo(() => {
    if (!externalInputVariables) return [];
    return externalInputVariables
      .map((nodeVar) => ({
        ...nodeVar,
        variables: nodeVar.variables.filter((v) =>
          ["string", "number", "str", "num", "float", "integer"].includes(
            v.type,
          ),
        ),
      }))
      .filter((nodeVar) => nodeVar.variables.length > 0);
  }, [externalInputVariables]);
  const [selectedRegistry, setSelectedRegistry] =
    useState<RegistryDropdownItem>();
  const [selectedAction, setSelectedAction] = useState<ActionItem>();
  const [scopeState, setScopeState] = useState<ScopeState>(initScopeState);
  const [multipleSelected, setMultipleSelected] = useState<boolean>(false);
  const [multipleCount, setMultipleCount] = useState<number>(50);
  const [variableName, setVariableName] = useState<string>("");
  const [conditions, setConditions] = useState<ExpedienteConditionData[]>([
    {
      id: new Date().getTime().toString(),
      variable: null,
      operation: null,
      comparativeValue: "",
      initialRangeValue: "",
      endRangeValue: "",
      logicalOperator: "and",
      operationValueType: "input",
      initialRangeValueType: "input",
      endRangeValueType: "input",
    },
  ]);
  // Initialize columns as empty - will be populated from entity fields or deserialization
  const [columns, setColumns] = useState<ValueColumn[]>([]);
  const [columnsDef, setColumnsDef] = useState<Variable[]>();
  const [currentNode, setCurrentNode] = useState<ApiNode | null>(null);
  const [logicalOperator, setLogicalOperator] =
    useState<LogicalOperator>("and");

  const { mutateAsync: getNode } = useGetNode();
  const [page, setPage] = useState<number>(1);
  const [hasMoreRegistries, setHasMoreRegistries] = useState<boolean>(false);
  const [registrySearchQuery, setRegistrySearchQuery] = useState("");
  const [isReadyToShow, setIsReadyToShow] = useState(false);

  // NEW: Entity selection state
  const [selectedEntityState, setSelectedEntityState] = useState<EntityType>();

  // NEW: Registry ID input states
  const [registryInputMode, setRegistryInputMode] =
    useState<RegistryInputMode>("inp");
  const [registryIdValue, setRegistryIdValue] = useState<string>("");
  const [registryVariable, setRegistryVariable] = useState<IfVariable | null>(
    null,
  );

  // NEW: Registry Name input states
  const [registryName, setRegistryName] = useState<string>("");
  const [registryNameInputType, setRegistryNameInputType] = useState<
    "inp" | "var"
  >("inp");
  const [registryNameVariable, setRegistryNameVariable] =
    useState<IfVariable | null>(null);

  // Link Users states
  const [linkAction, setLinkAction] = useState<LinkActionType>("link");
  const [linkUsers, setLinkUsers] = useState<LinkUserEntry[]>([
    {
      id: Date.now().toString(),
      document: "",
      documentInputType: "inp",
      email: "",
      emailInputType: "inp",
      roleId: undefined,
      roleInputType: "inp",
    },
  ]);

  // Link Expedients (Registros) states
  const [linkExpedientsAction, setLinkExpedientsAction] =
    useState<LinkExpedientsActionType>("link");
  const [linkExpedients, setLinkExpedients] = useState<LinkExpedientEntry[]>([
    {
      id: Date.now().toString(),
      expedientDocument: "",
      inputType: "inp",
    },
  ]);

  // Link Sub-Action state (users vs expedients)
  const [linkSubAction, setLinkSubAction] = useState<
    "link_users" | "link_expedients"
  >("link_users");

  // State to track entity fetched individually (not in main list)
  const [additionalEntity, setAdditionalEntity] = useState<EntityType | null>(
    null,
  );
  const [missingEntityId, setMissingEntityId] = useState<string | null>(null);

  // Fetch entities first (source of truth for grouping)
  const { data: dataEntities, isLoading: isLoadingEntities } = useGetEntities({
    per_page: 100,
  });

  // Fetch missing entity if needed
  // TODO: FETCH THE LAST VERSION OF THE ENTITY WHEN BACKEND PROVIDE IT
  // currently it fetches the saved version, even if the user has changed the entity in the node configuration,
  const { data: fetchedMissingEntity } = useGetEntity(
    missingEntityId ?? "",
    { includeFields: false, includeRoles: false },
    !!missingEntityId,
  );

  // When missing entity is fetched, set it as additional entity
  useEffect(() => {
    if (fetchedMissingEntity && missingEntityId) {
      setAdditionalEntity(fetchedMissingEntity);
      // Clear the missing entity id to prevent re-fetching
      setMissingEntityId(null);
    }
  }, [fetchedMissingEntity, missingEntityId]);

  // Get entities array - combine from query and additional fetched entity
  const entities = useMemo<EntityType[]>(() => {
    const baseEntities = dataEntities?.data ?? [];
    if (
      additionalEntity &&
      !baseEntities.some((e) => e.id === additionalEntity.id)
    ) {
      return [...baseEntities, additionalEntity];
    }
    return baseEntities;
  }, [dataEntities, additionalEntity]);

  // Fetch entity detail with fields and roles when an entity is selected
  const { data: fetchedEntityDetail, isLoading: isLoadingEntityDetail } =
    useGetEntity(
      selectedEntityState?.id ?? "",
      { includeFields: true, includeRoles: true },
      !!selectedEntityState?.id,
    );

  // Loading states derived from entity detail fetch
  const isLoadingEntityFields = isLoadingEntityDetail;
  const isLoadingEntityRoles = isLoadingEntityDetail;

  // Entity fields from the selected entity
  const entityFields = useMemo<FieldType[]>(() => {
    if (!fetchedEntityDetail?.fields) return [];
    return fetchedEntityDetail.fields;
  }, [fetchedEntityDetail]);

  // Entity roles from the selected entity
  const entityRoles = useMemo<EntityRoleType[]>(() => {
    if (!fetchedEntityDetail?.roles) return [];
    return fetchedEntityDetail.roles;
  }, [fetchedEntityDetail]);

  // Fetch registries for expediente node
  const {
    data: dataRegistries,
    isLoading: isLoadingRegistries,
    isFetching: isFetchingRegistries,
  } = useGetAllRegistriesForWorkflow({
    page,
    per_page: 100,
    ...(registrySearchQuery ? { filter: { search: registrySearchQuery } } : {}),
  });

  // Transform entities and registries into grouped dropdown items
  const dropdownItems = useMemo<ExpedienteDropdownItem[]>(() => {
    if (!dataEntities?.data || !dataRegistries?.data) return [];

    // Create a map of entity ID -> entity name from the entities data
    const entityIdToName = new Map<string, string>();
    for (const entity of dataEntities.data) {
      entityIdToName.set(entity.id, entity.name);
    }

    // Group registries by entity NAME (not ID) to avoid duplicates
    const grouped = new Map<
      string,
      { entityId: string; registries: RegistryType[] }
    >();

    for (const registry of dataRegistries.data) {
      const entityId = registry.expedient_base_entity_id;
      const entityName =
        entityIdToName.get(entityId) ||
        registry.base_entity?.name ||
        "Sin entidad";

      if (!grouped.has(entityName)) {
        grouped.set(entityName, { entityId, registries: [] });
      }
      grouped.get(entityName)!.registries.push(registry);
    }

    // Sort entity names alphabetically
    const sortedEntityNames = Array.from(grouped.keys()).sort((a, b) => {
      if (!a) return 1;
      if (!b) return -1;
      return a.localeCompare(b, "es", { sensitivity: "base" });
    });

    // Build flattened list with entity headers
    const items: ExpedienteDropdownItem[] = [];
    for (const entityName of sortedEntityNames) {
      const { entityId, registries } = grouped.get(entityName)!;

      // Sort registries alphabetically within each entity
      const sortedRegistries = [...registries].sort((a, b) => {
        if (!a.name) return 1;
        if (!b.name) return -1;
        return a.name.localeCompare(b.name, "es", { sensitivity: "base" });
      });

      // Add entity header
      items.push({
        id: entityId,
        name: entityName,
        type: "entity-header",
      });
      // Add registries under this entity
      for (const registry of sortedRegistries) {
        items.push({
          id: registry.id,
          name: registry.name,
          entityId: entityId,
          entityName: entityName,
          type: "registry",
        });
      }
    }

    return items;
  }, [dataEntities, dataRegistries]);

  // Get selected registry's entity fields for table detail simulation
  // TODO: This will need to be replaced with actual entity field fetching
  const isLoadingTableDetail = false;

  const hasDeserializedRef = useRef<string | null>(null);
  const { mutateAsync: updateNode } = useUpdateNode(flowId);

  const reset = () => {
    setScopeState(initScopeState);
    setMultipleSelected(false);
    setMultipleCount(50);
    setVariableName("");
    setRegistryName("");
    setRegistryNameInputType("inp");
    setRegistryNameVariable(null);
    setConditions([
      {
        id: new Date().getTime().toString(),
        variable: null,
        operation: null,
        comparativeValue: "",
        initialRangeValue: "",
        endRangeValue: "",
        logicalOperator: "and",
        operationValueType: "input",
        initialRangeValueType: "input",
        endRangeValueType: "input",
      },
    ]);
    setLogicalOperator("and");
  };

  /**
   * Generate value columns for create/update forms from entity fields
   * Only generates new columns if deserialization is complete and we have no data
   */
  useEffect(() => {
    if (!selectedAction || !entityFields || entityFields.length === 0) return;

    // Skip if we're still deserializing (indicated by partial ref OR if not yet fully deserialized)
    if (
      hasDeserializedRef.current &&
      (hasDeserializedRef.current.includes("_partial") ||
        hasDeserializedRef.current !== currentNode?.id)
    ) {
      return;
    }

    // If we have a currentNode with inputs and haven't fully deserialized yet, skip
    if (
      currentNode &&
      currentNode.inputs &&
      currentNode.inputs.length > 0 &&
      !hasDeserializedRef.current
    ) {
      return;
    }

    if (selectedAction.id === "create" || selectedAction.id === "update") {
      setRegistryName("");
      // Check if current columns already have data from deserialization
      // We check the state `columns` not the static `currentColumns`
      setColumns(() => {
        // const hasDeserializedData =
        //   prevColumns.length > 0 &&
        //   prevColumns.some(
        //     (col) =>
        //       col.value !== "" ||
        //       col.expiration !== undefined ||
        //       col.inputType === "variable",
        //   );

        // if (hasDeserializedData) {
        //   // Check if we need to update - only if fieldType is different
        //   const needsUpdate = prevColumns.some((col) => {
        //     const field = entityFields.find((f) => f.id === col.name);
        //     return field && col.fieldType !== field.field_type;
        //   });

        //   if (!needsUpdate) {
        //     return prevColumns; // No changes needed
        //   }

        //   // Merge deserialized data with entity fields to get updated field metadata
        //   return entityFields.map((field) => {
        //     const existingCol = prevColumns.find((c) => c.name === field.id);
        //     if (existingCol) {
        //       // Keep the deserialized data, but update field metadata
        //       return {
        //         ...existingCol,
        //         label: field.name,
        //         isRequired: field.is_required,
        //         fieldType: field.field_type,
        //         options:
        //           field.options ?? field.configuration?.options ?? undefined,
        //       };
        //     }
        //     // New field not in deserialized data - create default
        //     const valueType = mapEntityFieldTypeToValueInputType(
        //       field.field_type,
        //     );
        //     return {
        //       label: field.name,
        //       isRequired: field.is_required,
        //       name: field.id,
        //       valueType,
        //       placeholder:
        //         field.configuration?.placeholder ??
        //         getPlaceholderForType(valueType),
        //       value: field.default_value ?? "",
        //       inputType: "input" as const,
        //       fieldType: field.field_type,
        //       options:
        //         field.options ?? field.configuration?.options ?? undefined,
        //     };
        //   });
        // }

        // No deserialized data - generate fresh columns from entity fields
        return entityFields.map((field) => {
          const valueType = mapEntityFieldTypeToValueInputType(
            field.field_type,
          );
          const placeholder =
            field.configuration?.placeholder ??
            getPlaceholderForType(valueType);
          const defaultValue = field.default_value ?? "";

          return {
            label: field.name,
            isRequired: field.is_required,
            name: field.id,
            valueType,
            placeholder,
            value: defaultValue,
            inputType: "input" as const,
            fieldType: field.field_type,
            options: field.options ?? field.configuration?.options ?? undefined,
          };
        });
      });

      if (hasDeserializedRef.current) {
        hasDeserializedRef.current = null;
      }
    }
  }, [entityFields, selectedAction, currentNode]);

  /**
   * Set columnsDef from entity fields for variable definitions
   */
  useEffect(() => {
    if (!entityFields || entityFields.length === 0) {
      setColumnsDef([]);
      return;
    }

    // Convert entity fields to Variable format for columnsDef
    const variableDefs: Variable[] = entityFields.map((field) => {
      const valueInputType = mapEntityFieldTypeToValueInputType(
        field.field_type,
      );
      return {
        id: field.id,
        name: field.name,
        type: mapValueInputTypeToVariableType(valueInputType),
      };
    });
    setColumnsDef(variableDefs);
  }, [entityFields, fetchedEntityDetail]);

  /**
   * Reset columns and selected action when no registry is selected
   * ONLY applies to legacy system - new system uses selectedEntity
   */
  useEffect(() => {
    // Skip reset if using new system (entity-based) or if we've deserialized
    if (selectedEntityState || hasDeserializedRef.current) {
      return;
    }

    // Skip reset if we're in the process of deserializing (node is open and has expediente data)
    if (nodeDetailsOpen && currentNode) {
      const expedienteData = getExpedienteDataFromNode(currentNode);
      if (expedienteData && expedienteData.expedient_base_entity_id) {
        return;
      }
    }

    if (!selectedRegistry) {
      setColumns([]);
      setColumnsDef([]);
    }
  }, [selectedRegistry, currentNode, selectedEntityState, nodeDetailsOpen]);

  /**
   * Reset selected action when registry changes
   */
  useEffect(() => {
    if (
      selectedRegistry &&
      hasDeserializedRef.current &&
      !hasDeserializedRef.current.includes("_partial")
    ) {
      setSelectedAction(undefined);
    }
  }, [selectedRegistry]);

  /**
   * Reset page when search query changes
   */
  useEffect(() => {
    if (!registrySearchQuery || registrySearchQuery.trim() === "") {
      setPage(1);
    }
  }, [registrySearchQuery]);

  /**
   * Update pagination state on data load
   */
  useEffect(() => {
    if (!dataRegistries?.meta) return;
    const meta = dataRegistries.meta;
    const nextHasMore = meta ? meta.current_page < meta.last_page : false;
    setHasMoreRegistries(nextHasMore);
  }, [dataRegistries]);

  // handle pagination
  const loadMoreRegistries = () => {
    if (isLoadingRegistries || isFetchingRegistries) return;
    if (hasMoreRegistries) {
      setPage((p: number) => p + 1);
    }
  };

  /**
   * reset deserialization reference when node details are closed
   */
  useEffect(() => {
    if (nodeDetailsOpen === false) {
      hasDeserializedRef.current = null;
      setIsReadyToShow(false);
    }
  }, [nodeDetailsOpen]);

  /**
   * fetch node details when node is selected
   */
  useEffect(() => {
    if (!selectedNodeId || !nodeDetailsOpen) return;
    const run = async () => {
      try {
        const node = await getNode(selectedNodeId);
        if (node) setCurrentNode(node);
      } catch (error) {
        console.error("[Expediente] getNode error:", error);
      }
    };
    run();
  }, [selectedNodeId, nodeDetailsOpen, getNode]);

  /**
   * Deserialize when node details opened
   */
  useEffect(() => {
    if (!nodeDetailsOpen || !currentNode) return;
    if (hasDeserializedRef.current === currentNode.id) return;
    if (!dataEntities?.data) return;
    if (loadingVariables) return;

    if (!currentNode.inputs || currentNode.inputs.length === 0) {
      setIsReadyToShow(true);
      hasDeserializedRef.current = currentNode.id ?? "__unknown__";
      return;
    }

    // Get the expediente data from the node (new format with "data" input)
    const expedienteData = getExpedienteDataFromNode(currentNode);

    if (expedienteData) {
      // Find the entity by ID
      const entity = entities.find(
        (e) => e.id === expedienteData.expedient_base_entity_id,
      );
      if (entity) {
        setSelectedEntityState(entity);
      } else {
        // No matched entity found - try to fetch it individually
        setMissingEntityId(expedienteData.expedient_base_entity_id);
        hasDeserializedRef.current = `${currentNode.id}_waiting_entity`;
        return;
      }

      // Set the registry ID value
      const registerId = expedienteData.expedient_register_id || "";
      // If registerId is empty, check if there's a source_connection on the input
      // This handles the case where field_id and output_name are null in the connection
      if (!registerId) {
        const registerIdInput = currentNode.inputs?.find(
          (input) => input.name === "expedient_register_id",
        );
        if (
          registerIdInput?.source_connections &&
          registerIdInput.source_connections.length > 0
        ) {
          const sourceConn = registerIdInput.source_connections[0];

          // Find the variable using source_node_id
          const sourceNodeId = sourceConn.source_node_id;
          const nodeVar = inputVariables.find(
            (nv) => nv.nodeId === sourceNodeId,
          );

          if (nodeVar) {
            // Try to find by field_id first, then by output_name
            let foundVariable = null;
            if (sourceConn.field_id) {
              foundVariable = nodeVar.variables.find(
                (v) => v.id === sourceConn.field_id,
              );
            }
            if (!foundVariable && sourceConn.output_name) {
              foundVariable = nodeVar.variables.find(
                (v) =>
                  v.name === sourceConn.output_name ||
                  v.id === sourceConn.output_name,
              );
            }
            // If still not found, try source_node_output_id
            if (!foundVariable && sourceConn.source_node_output_id) {
              foundVariable = nodeVar.variables.find(
                (v) => v.id === sourceConn.source_node_output_id,
              );
            }
            // Fallback: For expedient_register_id, prefer a variable named "id" or with type "string"
            // This helps with legacy data where field_id wasn't saved
            if (!foundVariable && nodeVar.variables.length > 0) {
              // First, try to find a variable named "id" (case-insensitive)
              foundVariable = nodeVar.variables.find(
                (v) =>
                  v.name.toLowerCase() === "id" ||
                  v.name.toLowerCase() === "identificador",
              );
              // If not found, try to find a string type variable (not date)
              if (!foundVariable) {
                foundVariable = nodeVar.variables.find(
                  (v) =>
                    v.type &&
                    (v.type.toLowerCase() === "string" ||
                      v.type.toLowerCase() === "str"),
                );
              }
              // Last resort: get first variable from the node
              if (!foundVariable) {
                foundVariable = nodeVar.variables[0];
              }
            }

            if (foundVariable) {
              const variableWithNodeInfo = {
                ...foundVariable,
                node_id: nodeVar.nodeId,
                ref_name: nodeVar.ref_name || "",
              };
              setRegistryInputMode("var");
              setRegistryVariable(variableWithNodeInfo as IfVariable);

              // Skip the normal flow since we already set the variable
              // Get the action - check if it's link_expedients operation
              const operationInput = currentNode.inputs?.find(
                (input) => input.name === "operation",
              );
              const operation = operationInput?.value?.toString().toUpperCase();

              if (
                operation === "LINK_EXPEDIENTS" ||
                operation === "UNLINK_EXPEDIENTS"
              ) {
                const linkAction = actionList.find(
                  (a) => a.id === "link_users",
                );
                if (linkAction) {
                  setSelectedAction(linkAction);
                  setLinkSubAction("link_expedients");
                }
              } else {
                const action = getExpedienteNodeAction(currentNode, actionList);
                if (action) {
                  setSelectedAction(action);
                  if (action.id === "link_users") {
                    setLinkSubAction("link_users");
                  }
                }
              }
              hasDeserializedRef.current = `${currentNode.id}_partial`;
              return;
            }
          }
        }
      }

      if (registerId.startsWith("{{") && registerId.endsWith("}}")) {
        // It's a variable reference - could be variable name OR variable id (UUID)
        const variableRef = registerId.slice(2, -2);
        const variable = inputVariables
          .flatMap((node) =>
            (node.variables || []).map((v) => ({
              ...v,
              node_id: node.nodeId,
              ref_name: node.ref_name || "",
            })),
          )
          .find((v) => v.name === variableRef || v.id === variableRef);
        if (variable) {
          setRegistryInputMode("var");
          setRegistryVariable(variable as IfVariable);
        }
      } else {
        setRegistryInputMode("inp");
        setRegistryIdValue(registerId);
      }

      // Get the action - check if it's link_expedients operation
      const operationInput = currentNode.inputs?.find(
        (input) => input.name === "operation",
      );
      const operation = operationInput?.value?.toString().toUpperCase();

      // If operation is LINK_EXPEDIENTS or UNLINK_EXPEDIENTS, set action to link_users and sub-action to link_expedients
      if (
        operation === "LINK_EXPEDIENTS" ||
        operation === "UNLINK_EXPEDIENTS"
      ) {
        const linkAction = actionList.find((a) => a.id === "link_users");
        if (linkAction) {
          setSelectedAction(linkAction);
          setLinkSubAction("link_expedients");
        }
      } else {
        const action = getExpedienteNodeAction(currentNode, actionList);
        if (action) {
          setSelectedAction(action);
          // If it's link_users operation, set sub-action to link_users
          if (action.id === "link_users") {
            setLinkSubAction("link_users");
          }
        }
      }

      hasDeserializedRef.current = `${currentNode.id}_partial`;
    } else {
      setIsReadyToShow(true);
      hasDeserializedRef.current = currentNode.id ?? "__unknown__";
    }
  }, [
    nodeDetailsOpen,
    currentNode,
    loadingVariables,
    dataEntities,
    entities,
    inputVariables,
  ]);

  /**
   * Continue deserialization when missing entity is fetched and added to entities list
   */
  useEffect(() => {
    if (
      hasDeserializedRef.current?.endsWith("_waiting_entity") &&
      currentNode &&
      entities.length > 0
    ) {
      const expedienteData = getExpedienteDataFromNode(currentNode);
      if (expedienteData) {
        const entity = entities.find(
          (e) => e.id === expedienteData.expedient_base_entity_id,
        );
        if (entity) {
          setSelectedEntityState(entity);
          hasDeserializedRef.current = `${currentNode.id}_partial`;

          // Continue with registry ID deserialization
          const registerId = expedienteData.expedient_register_id || "";

          if (!registerId) {
            const registerIdInput = currentNode.inputs?.find(
              (input) => input.name === "expedient_register_id",
            );
            if (
              registerIdInput?.source_connections &&
              registerIdInput.source_connections.length > 0
            ) {
              const sourceConn = registerIdInput.source_connections[0];
              const sourceNodeId = sourceConn.source_node_id;
              const nodeVar = inputVariables.find(
                (nv) => nv.nodeId === sourceNodeId,
              );

              if (nodeVar) {
                let foundVariable = null;
                if (sourceConn.field_id) {
                  foundVariable = nodeVar.variables.find(
                    (v) => v.id === sourceConn.field_id,
                  );
                }
                if (!foundVariable && sourceConn.output_name) {
                  foundVariable = nodeVar.variables.find(
                    (v) =>
                      v.name === sourceConn.output_name ||
                      v.id === sourceConn.output_name,
                  );
                }
                if (!foundVariable && sourceConn.source_node_output_id) {
                  foundVariable = nodeVar.variables.find(
                    (v) => v.id === sourceConn.source_node_output_id,
                  );
                }
                if (!foundVariable && nodeVar.variables.length > 0) {
                  foundVariable = nodeVar.variables.find(
                    (v) =>
                      v.name.toLowerCase() === "id" ||
                      v.name.toLowerCase() === "identificador",
                  );
                  if (!foundVariable) {
                    foundVariable = nodeVar.variables.find(
                      (v) =>
                        v.type &&
                        (v.type.toLowerCase() === "string" ||
                          v.type.toLowerCase() === "str"),
                    );
                  }
                  if (!foundVariable) {
                    foundVariable = nodeVar.variables[0];
                  }
                }

                if (foundVariable) {
                  const variableWithNodeInfo = {
                    ...foundVariable,
                    node_id: nodeVar.nodeId,
                    ref_name: nodeVar.ref_name || "",
                  };
                  setRegistryInputMode("var");
                  setRegistryVariable(variableWithNodeInfo as IfVariable);
                }
              }
            }
          } else if (registerId.startsWith("{{") && registerId.endsWith("}}")) {
            const variableRef = registerId.slice(2, -2);
            const variable = inputVariables
              .flatMap((node) =>
                (node.variables || []).map((v) => ({
                  ...v,
                  node_id: node.nodeId,
                  ref_name: node.ref_name || "",
                })),
              )
              .find((v) => v.name === variableRef || v.id === variableRef);
            if (variable) {
              setRegistryInputMode("var");
              setRegistryVariable(variable as IfVariable);
            }
          } else {
            setRegistryInputMode("inp");
            setRegistryIdValue(registerId);
          }

          // Get the action
          const operationInput = currentNode.inputs?.find(
            (input) => input.name === "operation",
          );
          const operation = operationInput?.value?.toString().toUpperCase();

          if (
            operation === "LINK_EXPEDIENTS" ||
            operation === "UNLINK_EXPEDIENTS"
          ) {
            const linkAction = actionList.find((a) => a.id === "link_users");
            if (linkAction) {
              setSelectedAction(linkAction);
              setLinkSubAction("link_expedients");
            }
          } else {
            const action = getExpedienteNodeAction(currentNode, actionList);
            if (action) {
              setSelectedAction(action);
              if (action.id === "link_users") {
                setLinkSubAction("link_users");
              }
            }
          }
        }
      }
    }
  }, [entities, currentNode, inputVariables]);

  /**
   * Deserialize columns when entity fields are ready (for create/update)
   */
  useEffect(() => {
    if (!nodeDetailsOpen || !currentNode) return;
    if (hasDeserializedRef.current === currentNode.id) return;
    if (!selectedAction) return;
    if (loadingVariables) return;

    // For read, deactivate actions - just mark as ready
    if (["read", "deactivate"].includes(selectedAction.id)) {
      hasDeserializedRef.current = currentNode.id ?? "__unknown__";
      setIsReadyToShow(true);
      return;
    }

    // For link_users action - check linkSubAction to determine which data to deserialize
    if (selectedAction.id === "link_users") {
      if (linkSubAction === "link_users") {
        const linkUsersData = getDeserializedLinkUsers(
          currentNode,
          entityRoles,
          inputVariables,
        );
        if (linkUsersData) {
          setLinkAction(linkUsersData.linkAction);
          setLinkUsers(linkUsersData.linkUsers);
        }
      } else if (linkSubAction === "link_expedients") {
        const linkExpedientsData = getDeserializedLinkExpedients(
          currentNode,
          inputVariables,
        );
        if (linkExpedientsData) {
          setLinkExpedientsAction(linkExpedientsData.linkExpedientsAction);
          setLinkExpedients(linkExpedientsData.linkExpedients);
        }
      }
      hasDeserializedRef.current = currentNode.id ?? "__unknown__";
      setIsReadyToShow(true);
      return;
    }

    // For create and update - wait for entity fields to load and columnsDef to be ready
    if (isLoadingEntityFields) return;
    if (!columnsDef || columnsDef.length === 0) return;

    // Deserialize registry name for create and update actions
    if (selectedAction.id === "create" || selectedAction.id === "update") {
      const registryNameData = getDeserializedRegistryName(
        currentNode,
        inputVariables,
      );
      if (registryNameData) {
        setRegistryName(registryNameData.registryName);
        setRegistryNameInputType(registryNameData.registryNameInputType);
        setRegistryNameVariable(
          registryNameData.registryNameVariable as IfVariable,
        );
      }
    }

    // Only deserialize columns for create and update actions
    if (selectedAction.id === "create" || selectedAction.id === "update") {
      const expedienteColumns = getDeserializedExpedienteColumns(
        currentNode,
        columnsDef,
        inputVariables,
      );
      if (expedienteColumns && expedienteColumns.length > 0) {
        // Set the deserialized columns directly - they already have the correct structure
        setColumns(expedienteColumns);
      }
    }
    hasDeserializedRef.current = currentNode.id ?? "__unknown__";
    setIsReadyToShow(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    nodeDetailsOpen,
    currentNode,
    columnsDef,
    loadingVariables,
    selectedAction,
    entityRoles,
    isLoadingEntityFields,
    linkSubAction,
  ]);

  /**
   * Serialize and save the node
   */
  const saveNode = (title: string) => {
    if (selectedEntityState && selectedAction && currentNode) {
      // Get the expedient_register_id from input value or variable
      const expedientRegisterId =
        registryInputMode === "inp"
          ? registryIdValue
          : registryVariable
            ? `{{${registryVariable.id}}}`
            : "";
      // Determine the actual operation type based on selectedAction and linkSubAction
      const operationType =
        selectedAction.id === "link_users" ? linkSubAction : selectedAction.id;
      updateNode(
        getSerializedNode(
          operationType as ActionItemType,
          currentNode,
          title,
          flowId,
          selectedEntityState.id, // expedient_base_entity_id
          expedientRegisterId, // expedient_register_id
          multipleSelected,
          conditions,
          logicalOperator,
          scopeState,
          variableName,
          multipleCount,
          columns,
          linkAction,
          linkUsers,
          entityRoles,
          registryName,
          registryNameInputType,
          registryNameVariable,
          inputVariables, // Pass input variables for connection/field resolution
          linkExpedientsAction,
          linkExpedients,
          entityFields, // Pass entity fields for field_type lookup
        ),
      );
    }
  };

  const variableNameParse = variableNameSchema.safeParse(variableName);
  const isVariableNameValid = variableNameParse.success;
  const variableNameError = isVariableNameValid
    ? null
    : variableNameParse.error.issues[0]?.message || "Nombre inválido";

  return (
    <ExpedienteNodeContext.Provider
      value={{
        selectedAction,
        setSelectedAction,
        selectedRegistry,
        setSelectedRegistry,
        dropdownItems,
        scopeState,
        setScopeState,
        reset,
        setVariableName,
        variableName,
        isVariableNameValid,
        variableNameError,
        multipleSelected,
        setMultipleSelected,
        multipleCount,
        setMultipleCount,
        conditions,
        setConditions,
        logicalOperator,
        setLogicalOperator,
        columns,
        setColumns,
        columnsDef: columnsDef || [],
        setColumnsDef,
        currentNode,
        setCurrentNode,
        isLoadingTableDetail,
        saveNode,
        nodeName: currentNode?.display_name,
        loadMoreRegistries,
        hasMoreRegistries,
        isLoadingMoreRegistries: isFetchingRegistries,
        isReadyToShow,
        isValidScope:
          scopeState.conditionScope.name !== "ANY"
            ? (scopeState.conditionScope.needsComparativeValue &&
                !!scopeState.comparativeValue) ||
              !!(
                scopeState.conditionScope.needsComparativeRange &&
                !!scopeState.initialRange?.value &&
                !!scopeState.endRange?.value &&
                scopeState.dateRangeError === ""
              )
            : true,
        conditionsAreValid: conditions.every(
          (condition: ExpedienteConditionData) =>
            condition.variable &&
            condition.operation &&
            (!condition.operation.needsComparativeValue ||
              (condition.operation.needsComparativeValue &&
                condition.comparativeValue !== "")) &&
            (!condition.operation?.needsComparativeRange ||
              (condition.operation?.needsComparativeRange &&
                condition.initialRangeValue !== "" &&
                condition.endRangeValue !== "" &&
                isValidNumericRange(condition))),
        ),
        columnsAreValid: columns.every((col: ValueColumn) =>
          col.isRequired ? col.value !== "" : true,
        ),
        registrySearchQuery,
        setRegistrySearchQuery,
        entityFields,
        isLoadingEntityFields,
        // NEW: Entity selection
        entities,
        isLoadingEntities,
        selectedEntity: selectedEntityState,
        setSelectedEntity: setSelectedEntityState,
        // NEW: Registry ID input
        registryInputMode,
        setRegistryInputMode,
        registryIdValue,
        setRegistryIdValue,
        registryVariable,
        setRegistryVariable,
        inputVariables: filteredInputVariables,
        allInputVariables: externalInputVariables,
        // NEW: Registry Name input
        registryName,
        setRegistryName,
        registryNameInputType,
        setRegistryNameInputType,
        registryNameVariable,
        setRegistryNameVariable,
        registryNameIsValid:
          registryNameInputType === "inp"
            ? registryName.trim() !== ""
            : !!registryNameVariable,
        // Link Users
        linkAction,
        setLinkAction,
        linkUsers,
        setLinkUsers,
        entityRoles,
        isLoadingEntityRoles,
        linkUsersAreValid: linkUsers.every((user) => {
          const hasDocument =
            user.documentInputType === "var"
              ? !!user.document && typeof user.document !== "string"
              : typeof user.document === "string" &&
                user.document.trim() !== "";
          const hasEmail =
            user.emailInputType === "var"
              ? !!user.email && typeof user.email !== "string"
              : typeof user.email === "string" && user.email.trim() !== "";
          const hasRole =
            user.roleInputType === "var"
              ? !!user.roleId && typeof user.roleId !== "string"
              : typeof user.roleId === "string" && user.roleId.trim() !== "";

          // Require at least one identifier (document or email)
          const hasIdentifier = hasDocument || hasEmail;

          // For "link" action, also require a role
          // For "unlink" action, only require identifier
          if (linkAction === "link") {
            return hasIdentifier && hasRole;
          } else {
            return hasIdentifier;
          }
        }),
        // Link Expedients (Registros)
        linkExpedientsAction,
        setLinkExpedientsAction,
        linkExpedients,
        setLinkExpedients,
        linkExpedientsAreValid: linkExpedients.every((expedient) => {
          const hasExpedientDocument =
            expedient.inputType === "var"
              ? !!expedient.expedientDocument &&
                typeof expedient.expedientDocument !== "string"
              : typeof expedient.expedientDocument === "string" &&
                expedient.expedientDocument.trim() !== "";
          return hasExpedientDocument;
        }),
        // Link Sub-Action selector
        linkSubAction,
        setLinkSubAction,
      }}
    >
      {children}
    </ExpedienteNodeContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useExpedienteNode = () => {
  if (!ExpedienteNodeContext) {
    throw new Error(
      "useExpedienteNode must be used within an ExpedienteNodeProvider.",
    );
  }
  return useContext(ExpedienteNodeContext);
};
